"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
} from "@/components/tool-kit/styles";
import { useRun } from "@/components/tools/reference-tools/use-run";
import {
  ChaseResults,
  type Finished,
} from "@/components/tools/research-discovery/chase-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { runChase } from "@/lib/tool-kit/research-discovery/chase-run";
import {
  FORWARD_CAP,
  type ForwardOrder,
  MAX_SEEDS,
  planSeeds,
} from "@/lib/tool-kit/research-discovery/citation-chaser";

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_CHASER_STRINGS = {
  inputLabel: "Seed articles — one DOI per line",
  inputPlaceholder:
    "10.1073/pnas.0507655102\nhttps://doi.org/10.1371/journal.pone.0115253",
  inputHint:
    "Up to {max} DOIs. Each is looked up on OpenAlex, then its reference list and its citing works are fetched, 100 to a request.",
  orderLabel: "If a seed has more than {cap} citing works, fetch:",
  orderNewest: "Newest {cap}",
  orderMostCited: "Most-cited {cap}",
  chase: "Chase citations",
  working: "Request {done} of {total}…",
  cancel: "Stop",
  privacy:
    "Pressing the button sends the DOIs, and then OpenAlex work IDs, from your browser to api.openalex.org, OpenAlex's public API. Nothing goes to CitationEasy and nothing is saved. OpenAlex gives keyless visitors a small daily budget shared by everyone on the same network, so a chase costs a handful of requests, not hundreds.",
  emptyPrompt: "Paste one to {max} DOIs and press Chase citations.",
  unexpected: "Something went wrong during the search. Please try again.",
  incomplete:
    "one:{count} request failed, so these lists are incomplete. Wait a minute and run the chase again.|other:{count} requests failed, so these lists are incomplete. Wait a minute and run the chase again.",
  noneFound:
    "None of those DOIs is in OpenAlex, so there is nothing to chase. Check the DOIs, or try a different seed.",
  seedsHeading: "Seed articles",
  seedStates: {
    found: "Found",
    missing: "Not in OpenAlex",
    failed: "Lookup failed — try again",
    invalid: "No DOI in this line",
    duplicate: "Repeats an earlier line",
    over: "Over the limit of {max} seeds",
    skipped: "Skipped",
  },
  seedDetail: "{year} · cites {refs} works in OpenAlex · cited by {citedBy}",
  resultsHeading: "Citations found",
  statBackward: "Backward (references)",
  statForward: "Forward (cited by)",
  statBoth: "In both lists",
  backwardTab: "Backward · {count}",
  forwardTab: "Forward · {count}",
  viewLabel: "Direction",
  backwardNote:
    "Works the seeds cite, with the seeds themselves removed. {total} distinct references in all.",
  backwardCut:
    "Only the {cap} cited by the most seeds were fetched, and the export covers those.",
  forwardNote:
    "Works that cite a seed, with the seeds removed. OpenAlex's citation counts for the seeds add up to {total}; the list can be smaller because it leaves out the seeds themselves and citing works OpenAlex cannot list.",
  forwardCut:
    "Only {cap} per seed were fetched, so this list is a sample, not every citing work.",
  emptyList: "Nothing in this list.",
  columns: {
    work: "Work",
    seeds: "Seeds linked",
    citedBy: "Cited by",
  },
  etAl: "et al.",
  untitled: "Untitled work",
  noDoi: "No DOI",
  tableCaption: "Works found by citation chasing",
  showMore: "Show {count} more",
  copyDois: "Copy DOIs",
  copyTable: "Copy table",
  downloadRis: "Download RIS",
  downloadBoth: "Download RIS for both lists",
  copied: "Copied!",
  recordHeading: "Search record for your methods section",
  recordText:
    "Backward and forward citation searching of {seeds} seed articles was run on {date} using OpenAlex (api.openalex.org) through CitationEasy's Citation Chaser. Backward searching found {backward} records and forward searching found {forward} records after the seed articles were removed.",
  recordNewest:
    " For each seed, up to {cap} of the newest citing works were retrieved.",
  recordMostCited:
    " For each seed, up to {cap} of the most-cited citing works were retrieved.",
  recordCopy: "Copy record",
  dedupeNote:
    "Combined, the two lists hold {union} different works. Duplicates against your database search still need removing in your reference manager.",
  indexNote:
    "OpenAlex builds reference lists from publisher metadata and full-text extraction, so a seed's list can be shorter than the printed one and recent citations can lag. Treat the result as leads to screen, not a complete search.",
  fileBackward: "citation-chaser-backward",
  fileForward: "citation-chaser-forward",
  fileBoth: "citation-chaser-all",
} as const;

export default function CitationChaserWidget() {
  const strings = useToolUi(CITATION_CHASER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [order, setOrder] = useState<ForwardOrder>("newest");
  const [finished, setFinished] = useState<Finished | null>(null);
  const [failed, setFailed] = useState(false);
  const run = useRun();

  async function handleChase() {
    const lines = planSeeds(text);
    setFailed(false);
    setFinished(null);
    const controller = run.start(
      lines.filter((l) => l.state === "queued").length
    );
    try {
      const result = await runChase(
        lines,
        order,
        controller.signal,
        (done, total) => run.advance(controller, done, total)
      );
      if (result && run.isCurrent(controller)) {
        const date = new Intl.DateTimeFormat(locale, {
          dateStyle: "long",
        }).format(new Date());
        setFinished({ result, date, order });
      }
    } catch {
      if (run.isCurrent(controller)) {
        setFailed(true);
      }
    } finally {
      run.finish(controller);
    }
  }

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={t(strings.inputHint, { max: fmt(MAX_SEEDS) })}
        id="cch-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={4}
        value={text}
      />
      <PillGroup
        label={t(strings.orderLabel, { cap: fmt(FORWARD_CAP) })}
        name="cch-order"
        onChange={setOrder}
        options={[
          {
            value: "newest",
            label: t(strings.orderNewest, { cap: fmt(FORWARD_CAP) }),
          },
          {
            value: "most-cited",
            label: t(strings.orderMostCited, { cap: fmt(FORWARD_CAP) }),
          },
        ]}
        value={order}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={run.running || text.trim() === ""}
          onClick={handleChase}
          type="button"
        >
          {run.progress
            ? t(strings.working, {
                done: fmt(Math.min(run.progress.done + 1, run.progress.total)),
                total: fmt(run.progress.total),
              })
            : strings.chase}
        </button>
        {run.running ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={run.cancel}
            type="button"
          >
            {strings.cancel}
          </button>
        ) : null}
      </div>
      <p className={HINT}>{strings.privacy}</p>

      <div aria-live="polite" className="space-y-6">
        {failed ? <ErrorNotice>{strings.unexpected}</ErrorNotice> : null}
        {finished || run.running || failed ? null : (
          <Notice>{t(strings.emptyPrompt, { max: fmt(MAX_SEEDS) })}</Notice>
        )}
        {finished ? (
          <ChaseResults finished={finished} strings={strings} />
        ) : null}
      </div>
    </section>
  );
}

export type CitationChaserStrings = typeof CITATION_CHASER_STRINGS;
